/**
 * Preserve Focus Across Streams
 *
 * Un remplacement Turbo Stream (`replace` / `update`) ne doit jamais faire perdre
 * le focus clavier. Quand l'élément focalisé vit DANS une cible remplacée, le
 * nœud part avec elle et le navigateur rend le focus à `<body>` : un utilisateur
 * au clavier ou au lecteur d'écran est renvoyé en haut de la page, sans un mot.
 *
 * Cas mesuré (PR #731, F10) : sur la page d'un CR, « Fermer » la modale de
 * progression rend le focus à « Ajouter une annexe » ; la diffusion de la
 * section 9 remplace ensuite ce bouton, et le focus retombait sur `<body>`.
 *
 * Le geste, générique : sur `turbo:before-stream-render`, on enveloppe
 * `event.detail.render`. JUSTE AVANT le rendu (et non au dispatch : Turbo 8
 * attend une frame entre les deux, pendant laquelle le focus peut encore
 * bouger), si l'élément actif est dans une des `targetElements`, on relève ses
 * clés d'équivalence. Après le rendu, SI le focus a été perdu (`<body>`, rien,
 * ou un nœud détaché), on focalise le premier équivalent RENDU. Le focus posé
 * ailleurs entre-temps n'est jamais volé ; un focus hors des cibles n'est
 * jamais touché ; le rendu d'origine est toujours appelé.
 *
 * Clés d'équivalence, par ordre de priorité : `id`, puis
 * `data-modal-opener-id-value`, puis `data-slide-over-trigger-src-value`.
 * Seule la PRÉSENCE d'un attribut entre dans un sélecteur ; la valeur se compare
 * en boucle, jamais interpolée dans du CSS (même idiome que
 * `modal_controller.js#renderedOpenerEquivalent`).
 *
 * @example
 * import { preserveFocusAcrossStreams } from './utils/preserve_focus_across_streams';
 * preserveFocusAcrossStreams();
 */

const EQUIVALENCE_ATTRIBUTES = ['data-modal-opener-id-value', 'data-slide-over-trigger-src-value'];
const REPLACING_ACTIONS = new Set(['replace', 'update']);
const INSTALLED = Symbol.for('jerico.preserveFocusAcrossStreams');

/**
 * @typedef {{ attribute: string, value: string }} FocusKey
 * `attribute` vaut `'id'` ou l'un des `EQUIVALENCE_ATTRIBUTES`.
 */

/**
 * @param {Document} doc
 * @returns {boolean} vrai quand rien d'utile n'a le focus
 */
export function focusLost(doc) {
  const active = doc.activeElement;
  return !active || active === doc.body || active === doc.documentElement || !active.isConnected;
}

/**
 * @param {Element} element
 * @returns {Array<FocusKey>} clés présentes, par ordre de priorité
 */
export function focusKeys(element) {
  const keys = [];
  const id = element.getAttribute('id');
  if (id) keys.push({ attribute: 'id', value: id });
  EQUIVALENCE_ATTRIBUTES.forEach((attribute) => {
    const value = element.getAttribute(attribute);
    if (value) keys.push({ attribute, value });
  });
  return keys;
}

/**
 * @param {Element} element
 * @returns {boolean}
 */
function isRendered(element) {
  return Boolean(element) && element.getClientRects().length > 0;
}

/**
 * @param {Array<FocusKey>} keys
 * @param {Document} doc
 * @returns {Element|undefined} premier équivalent RENDU, clé prioritaire d'abord
 */
export function renderedEquivalent(keys, doc) {
  for (const { attribute, value } of keys) {
    const candidates =
      attribute === 'id'
        ? [doc.getElementById(value)]
        : Array.from(doc.querySelectorAll(`[${attribute}]`)).filter(
            (candidate) => candidate.getAttribute(attribute) === value
          );
    const found = candidates.find(isRendered);
    if (found) return found;
  }
  return undefined;
}

/**
 * @param {Element} stream l'élément `<turbo-stream>`
 * @returns {Array<Element>} cibles, vide si l'action n'en résout aucune
 */
function targetsOf(stream) {
  try {
    return Array.from(stream.targetElements || []);
  } catch {
    return [];
  }
}

/**
 * @param {Element} stream
 * @param {Document} doc
 * @returns {Array<FocusKey>} clés de l'élément actif s'il vit dans une cible
 */
function keysOfFocusInside(stream, doc) {
  if (focusLost(doc)) return [];
  const active = doc.activeElement;
  const inside = targetsOf(stream).some((target) => target === active || target.contains(active));
  return inside ? focusKeys(active) : [];
}

/**
 * Enveloppe le rendu d'un `turbo:before-stream-render`.
 *
 * @param {CustomEvent} event
 * @param {Document} doc
 */
export function wrapStreamRender(event, doc = document) {
  const stream = event.target;
  const detail = event.detail;
  if (!stream || !detail || typeof detail.render !== 'function') return;
  if (!REPLACING_ACTIONS.has(stream.getAttribute('action'))) return;

  const originalRender = detail.render;
  detail.render = async (streamElement) => {
    const keys = keysOfFocusInside(stream, doc);
    await originalRender(streamElement);
    if (keys.length === 0 || !focusLost(doc)) return;

    renderedEquivalent(keys, doc)?.focus({ preventScroll: true });
  };
}

/**
 * Pose l'écouteur global, une seule fois par document.
 *
 * @param {Document} doc
 * @returns {() => void} retrait de l'écouteur (tests)
 */
export function preserveFocusAcrossStreams(doc = document) {
  if (doc[INSTALLED]) return doc[INSTALLED];

  const listener = (event) => wrapStreamRender(event, doc);
  doc.addEventListener('turbo:before-stream-render', listener);
  const teardown = () => {
    doc.removeEventListener('turbo:before-stream-render', listener);
    delete doc[INSTALLED];
  };
  doc[INSTALLED] = teardown;
  return teardown;
}
